import { useState, useEffect, useRef } from 'react'
import { ShieldAlert } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@shared/components/ui/dialog'
import { Button } from '@shared/components/ui/button'
import { Label } from '@shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select'
import { FormTextarea } from '@shared/components/forms/fields/FormTextarea'
import { DialogResultTransition, type DialogStatus } from '@shared/components/ui/DialogResultTransition'
import type { FraudAlertFormData, FraudAlertAction } from '../types'

interface FraudAlertFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: FraudAlertFormData) => Promise<void> | void
  onSuccess?: () => void
  /** Risk score that triggered the alert, e.g. "87" */
  riskScore?: string | number
}

const RESOLUTION_REASONS = [
  { value: 'false_positive',      label: 'False positive' },
  { value: 'customer_confirmed',  label: 'Customer confirmed' },
  { value: 'resolved_internally', label: 'Resolved internally' },
  { value: 'low_risk_on_review',  label: 'Low risk after review' },
  { value: 'other',               label: 'Other' },
]

const ESCALATION_TEAMS = [
  'Risk team',
  'Compliance',
  'Legal',
  'Management',
]

const ACTION_OPTIONS: Array<{ value: FraudAlertAction; label: string }> = [
  { value: 'resolve',  label: 'Resolve alert' },
  { value: 'escalate', label: 'Escalate' },
]

const AUTO_CLOSE_MS = 1500

export function FraudAlertForm({
  open,
  onClose,
  onSubmit,
  onSuccess,
  riskScore,
}: FraudAlertFormProps) {
  const [action, setAction]                 = useState<FraudAlertAction>('resolve')
  const [resolution, setResolution]         = useState('')
  const [escalationTeam, setEscalationTeam] = useState('')
  const [notes, setNotes]                   = useState('')
  const [isLoading, setIsLoading]           = useState(false)
  const [status, setStatus]                 = useState<DialogStatus>('idle')
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (open) {
      setAction('resolve')
      setResolution('')
      setEscalationTeam('')
      setNotes('')
      setIsLoading(false)
      setStatus('idle')
    }
    return () => clearTimeout(timerRef.current)
  }, [open])

  const isValid =
    (action === 'resolve' && resolution.length > 0) ||
    (action === 'escalate' && escalationTeam.length > 0)

  async function handleSubmit() {
    if (!isValid || isLoading) return
    setIsLoading(true)
    try {
      await onSubmit({
        action,
        resolution: action === 'resolve' ? resolution : undefined,
        escalationTeam: action === 'escalate' ? escalationTeam : undefined,
        notes: notes.trim(),
      })
      setStatus('success')
      timerRef.current = setTimeout(() => {
        onSuccess?.()
        onClose()
      }, AUTO_CLOSE_MS)
    } catch {
      setStatus('error')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && status !== 'success') onClose() }}>
      <DialogContent className="max-w-md">
        <DialogResultTransition
          status={status}
          successMessage={action === 'resolve' ? 'Fraud alert resolved' : 'Fraud alert escalated'}
          errorMessage="Failed to process fraud alert"
          errorDescription="Something went wrong. Please try again."
          onRetry={() => setStatus('idle')}
          onClose={onClose}
        >
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <ShieldAlert className="h-5 w-5 text-destructive" />
              <DialogTitle>Review fraud alert</DialogTitle>
            </div>
            <DialogDescription>
              Review the flagged payment and decide how to proceed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {riskScore !== undefined && (
              <div className="rounded-lg bg-muted/50 p-4 space-y-1">
                <div className="text-xs text-muted-foreground font-mono uppercase tracking-wide">Risk score</div>
                <div className="text-sm font-medium">{riskScore}</div>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-medium">
                How would you like to handle this? <span className="text-destructive">*</span>
              </Label>
              <Select value={action} onValueChange={(v) => setAction(v as FraudAlertAction)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select action..." />
                </SelectTrigger>
                <SelectContent>
                  {ACTION_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {action === 'resolve' && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Resolution reason <span className="text-destructive">*</span>
                </Label>
                <Select value={resolution} onValueChange={setResolution}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    {RESOLUTION_REASONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {action === 'escalate' && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Escalate to <span className="text-destructive">*</span>
                </Label>
                <Select value={escalationTeam} onValueChange={setEscalationTeam}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select team..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ESCALATION_TEAMS.map((team) => (
                      <SelectItem key={team} value={team}>
                        {team}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <FormTextarea
              label="Notes"
              description="Additional context for this action (optional)."
              value={notes}
              onChange={setNotes}
              placeholder="Add any additional context..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={onClose} className="font-medium">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isValid || isLoading}
              className="rounded-full px-6"
            >
              {isLoading
                ? 'Submitting...'
                : action === 'resolve'
                ? 'Resolve alert'
                : 'Escalate'}
            </Button>
          </DialogFooter>
        </DialogResultTransition>
      </DialogContent>
    </Dialog>
  )
}
